/**
 * ReadingMismatchModal — prompts an operator when their typed starting_hours
 * or starting_kms differs from the machine's last recorded reading.
 *
 * Trigger: backend returns 409 with detail.code === "READING_MISMATCH".
 * Asks: why is the reading different and who/what caused it.
 * On save: re-submits the original usage start with `reconciliation_reason` populated.
 */
import React, { useState } from "react";
import { AlertTriangle, Save, X } from "lucide-react";
import { Button, Modal, Textarea, Select } from "../shared/ui";

const REASONS = [
  { value: "operator_error", label: "Last operator wrote it down wrong" },
  { value: "untracked_use", label: "Machine was used by someone who didn't log it" },
  { value: "fault_repair", label: "Workshop / repair work since last reading" },
  { value: "odometer_reset", label: "Odometer was reset / replaced" },
  { value: "data_entry_typo", label: "Typo in the previous entry" },
  { value: "other", label: "Other (explain below)" },
];

export const ReadingMismatchModal = ({ mismatch, onClose, onConfirm }) => {
  const [reason, setReason] = useState("operator_error");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);

  if (!mismatch) return null;
  const fmtV = (v) => (v == null ? "—" : v);

  const submit = async () => {
    const reasonLabel = REASONS.find((r) => r.value === reason)?.label || reason;
    const finalReason = details ? `${reasonLabel} — ${details}` : reasonLabel;
    setSaving(true);
    try {
      await onConfirm(finalReason);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Odometer / Hours Don't Match" size="md">
      <div className="space-y-4" data-testid="reading-mismatch-modal">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <p className="font-bold text-amber-900">
              {mismatch.machine_name} — last recorded reading doesn't match what you've entered.
            </p>
            {mismatch.last_recorded_by && (
              <p className="text-xs text-amber-800 mt-1">
                Last logged by <strong>{mismatch.last_recorded_by}</strong>
                {mismatch.last_recorded_at ? ` on ${new Date(mismatch.last_recorded_at).toLocaleDateString("en-AU")}` : ""}
              </p>
            )}
          </div>
        </div>

        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-3 bg-slate-100 text-xs uppercase font-bold text-slate-600">
            <div className="p-2">Field</div>
            <div className="p-2">Last Reading</div>
            <div className="p-2">You Entered</div>
          </div>
          {(mismatch.discrepancies || []).map((d, idx) => (
            <div key={idx} className="grid grid-cols-3 border-t">
              <div className="p-2 font-bold text-sm">
                {d.field === "starting_hours" ? "Hours" : "Kms"}
              </div>
              <div className="p-2 text-sm">{fmtV(d.expected)}</div>
              <div className="p-2 text-sm text-rose-600 font-bold">{fmtV(d.supplied)}</div>
            </div>
          ))}
        </div>

        <Select
          label="Why is the reading different?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          options={REASONS}
          testId="reading-mismatch-reason"
        />
        <Textarea
          label="Extra detail (who, when, what happened)"
          placeholder="e.g. Bob took it for a delivery yesterday and didn't clock on..."
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          rows={3}
          testId="reading-mismatch-details"
        />

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="reading-mismatch-confirm">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Confirm & Continue"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReadingMismatchModal;
